import * as projection from "@arcgis/core/geometry/projection";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Geometry from "@arcgis/core/geometry/Geometry";
import Graphic from "@arcgis/core/Graphic";
import { PhaseErrorType } from "../components/features/master-create-study/main-form-create-study/main-form-create-study";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import MapView from "@arcgis/core/views/MapView";
import { Layer } from "../interfaces/layer";
import { LayersDataTypes } from "../types/layers-data-types";
import MapLayer from "@arcgis/core/layers/Layer";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import {
  CreatedObjectInfrastructureResults,
  InfrastructureTypeKey,
} from "../services/reducers/master-create-study";
import { BufferInfrastructureResults } from "../interfaces/master-create-study";

type LinearUnits = "meters" | "kilometers";

export const verifyPhaseTerritory = async (
  outerGeometry: Geometry,
  innerGeometry: Graphic,
): Promise<{ isContains: boolean; projectedInnerGeometry: Graphic }> => {
  let transformedGeometry = innerGeometry.geometry;

  if (
    outerGeometry.spatialReference.wkid !==
    innerGeometry.geometry.spatialReference.wkid
  ) {
    await projection.load();

    const projected = projection.project(
      innerGeometry.geometry,
      outerGeometry.spatialReference,
    );

    if (!projected || Array.isArray(projected)) {
      return {
        isContains: false,
        projectedInnerGeometry: innerGeometry.clone(),
      };
    }

    transformedGeometry = projected;
  }

  const isContains = geometryEngine.contains(
    outerGeometry,
    transformedGeometry,
  );

  const projectedInnerGeometry = innerGeometry.clone();
  projectedInnerGeometry.geometry = transformedGeometry;

  return {
    isContains,
    projectedInnerGeometry,
  };
};

export const getPhaseTooltipText = (
  errorType: PhaseErrorType,
  t: (key: string) => string,
): string => {
  switch (errorType) {
    case "notPolygon":
      return t("main-form-create-study.phaseErrorNotPolygon");
    case "sameAsSiteBoundary":
      return t("main-form-create-study.phaseErrorSameBoundary");
    default:
      return "";
  }
};

export const supportsQueryFeatures = (
  layer: unknown,
): layer is FeatureLayer | GeoJSONLayer => {
  return layer instanceof FeatureLayer || layer instanceof GeoJSONLayer;
};

export async function checkLayerHasFeatures(
  viewer: MapView,
  layer: Layer,
): Promise<boolean> {
  try {
    const mapLayer = viewer.map.layers.find((l) => l.id === layer.name);

    if (!mapLayer) {
      return false;
    }

    await mapLayer.load();

    if (mapLayer.type === "feature") {
      const featureLayer = mapLayer as FeatureLayer;

      try {
        const count = await featureLayer.queryFeatureCount({ where: "1=1" });
        return count > 0;
      } catch {
        return false;
      }
    }

    if (mapLayer.type === "map-image") {
      const mapImageLayer = mapLayer as MapImageLayer;

      const sublayers = mapImageLayer.allSublayers.filter(
        (s) => typeof s.createFeatureLayer === "function",
      );

      if (sublayers.length === 0) {
        return false;
      }

      const results = await Promise.allSettled(
        sublayers.map(async (sublayer) => {
          try {
            const featureLayer = await sublayer.createFeatureLayer();
            if (!featureLayer) return false;

            const count = await featureLayer.queryFeatureCount({
              where: "1=1",
            });

            return count > 0;
          } catch {
            return false;
          }
        }),
      );

      return results.some(
        (result) => result.status === "fulfilled" && result.value,
      );
    }

    return false;
  } catch {
    return false;
  }
}

function getProjectedDistance(
  geomFrom: Geometry,
  geomTo: Geometry,
  unit: LinearUnits,
) {
  if (!geomFrom || !geomTo) return null;

  if (geomFrom.spatialReference?.wkid !== geomTo.spatialReference?.wkid) {
    const projected = projection.project(geomFrom, geomTo.spatialReference);
    if (!Array.isArray(projected)) {
      geomFrom = projected;
    } else {
      return null;
    }
  }

  return geometryEngine.distance(geomTo, geomFrom, unit);
}

export async function findMuscatAirportGeometry(airportLayers: MapLayer[]) {
  for (const layer of airportLayers) {
    if (!supportsQueryFeatures(layer)) continue;
    const result = await layer.queryFeatures({
      where: `NameEn = 'Muscat International Airport'`,
      outFields: ["*"],
      returnGeometry: true,
    });
    if (result.features.length > 0) {
      return result.features[0].geometry;
    }
  }
  return null;
}

export async function findNearestSeaports(
  seaportLayers: MapLayer[],
  targetGeom: Geometry,
) {
  const nearestPorts = [];

  for (const layer of seaportLayers) {
    if (!supportsQueryFeatures(layer)) continue;

    const result = await layer.queryFeatures({
      where: "1=1",
      outFields: ["*"],
      returnGeometry: true,
    });

    for (const feature of result.features) {
      const distance = getProjectedDistance(
        feature.geometry,
        targetGeom,
        "kilometers",
      );
      if (distance !== null) {
        nearestPorts.push({
          name: feature.attributes?.NameEn,
          distance: distance,
        });
      }
    }
  }

  return nearestPorts
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 2)
    .map((p) => ({
      name: p.name,
      distance: Math.round(p.distance),
    }));
}

export async function getInfrastructureData(
  layersConfig: Layer[],
  viewer: MapView,
  coordGeometry: Geometry,
  bufferGeometry: Geometry,
): Promise<{
  bufferResults: BufferInfrastructureResults;
  directionResults: CreatedObjectInfrastructureResults;
}> {
  const bufferResults = {} as BufferInfrastructureResults;
  const directionResults = {} as CreatedObjectInfrastructureResults;

  const infrastructureChecks: Array<{
    dataType: LayersDataTypes;
    resultKey: keyof BufferInfrastructureResults;
  }> = [
    {
      dataType: LayersDataTypes.ElectricityInfrastructure,
      resultKey: "powerSupply",
    },
    { dataType: LayersDataTypes.WaterInfrastructure, resultKey: "water" },
    { dataType: LayersDataTypes.TelecomInfrastructure, resultKey: "telecom" },
    { dataType: LayersDataTypes.SewerageInfrastructure, resultKey: "drainage" },
    { dataType: LayersDataTypes.GasInfrastructure, resultKey: "gas" },
  ];

  for (const { dataType, resultKey } of infrastructureChecks) {
    try {
      const matchingLayers = layersConfig
        .filter((l) => l.dataType === dataType)
        .map((l) => l.name);

      let minDistanceMeters: number | null = null;
      let nearestFeatureGeometry: Geometry | null = null;

      for (const name of matchingLayers) {
        const layer = viewer.map.layers.find((l) => l.id === name);
        if (!supportsQueryFeatures(layer)) continue;

        const result = await layer.queryFeatures({
          geometry: bufferGeometry,
          spatialRelationship: "intersects",
          outFields: ["*"],
          returnGeometry: true,
        });

        for (const feature of result.features) {
          if (!feature.geometry) continue;
          const distMeters = getProjectedDistance(
            feature.geometry,
            coordGeometry,
            "meters",
          );
          if (
            distMeters !== null &&
            (minDistanceMeters === null || distMeters < minDistanceMeters)
          ) {
            minDistanceMeters = distMeters;
            nearestFeatureGeometry = feature.geometry;
          }
        }
      }

      bufferResults[resultKey] =
        minDistanceMeters !== null
          ? { available: true, distance: Math.round(minDistanceMeters) }
          : { available: false, distance: 0 };

      let direction: string | null = null;
      if (
        nearestFeatureGeometry &&
        minDistanceMeters !== null &&
        minDistanceMeters > 0
      ) {
        const projected = projection.project(
          nearestFeatureGeometry,
          coordGeometry.spatialReference,
        );
        if (projected && !Array.isArray(projected)) {
          direction = getVectorDirection(coordGeometry, projected);
        }
      }

      directionResults[dataType as InfrastructureTypeKey] = {
        distance:
          minDistanceMeters !== null
            ? Number((minDistanceMeters / 1000).toFixed(2))
            : null,
        direction,
      };
    } catch (err) {
      console.error(`[infrastructure] error for ${resultKey}:`, err);
      bufferResults[resultKey] = { available: false, distance: 0 };
      directionResults[dataType as InfrastructureTypeKey] = {
        distance: null,
        direction: null,
      };
    }
  }

  return { bufferResults, directionResults };
}

export async function getLogisticsDistances(
  layersConfig: Layer[],
  viewer: MapView,
  coordGeometry: Geometry,
) {
  const allLayers = viewer.map.layers.toArray();

  const airportLayers = allLayers.filter((l) =>
    layersConfig.some(
      (layerConfig) =>
        layerConfig.name === l.id &&
        layerConfig.dataType === LayersDataTypes.LogisticInfrastructure &&
        layerConfig.name === "OmanAirports",
    ),
  );

  const seaportLayers = allLayers.filter((l) =>
    layersConfig.some(
      (layerConfig) =>
        layerConfig.name === l.id &&
        layerConfig.dataType === LayersDataTypes.LogisticInfrastructure &&
        layerConfig.name === "OmanSeaports",
    ),
  );

  const muscatAirportGeometry = await findMuscatAirportGeometry(airportLayers);

  const airportDistance = muscatAirportGeometry
    ? Math.round(
        getProjectedDistance(
          muscatAirportGeometry,
          coordGeometry,
          "kilometers",
        ) ?? 0,
      )
    : null;

  const [nearestPort, secondNearestPort] = await findNearestSeaports(
    seaportLayers,
    coordGeometry,
  );

  return {
    airportDistance,
    FirstPortName: nearestPort?.name || null,
    FirstPortDist: nearestPort?.distance || null,
    SecondPortName: secondNearestPort?.name || null,
    SecondPortDist: secondNearestPort?.distance || null,
  };
}

function getCenterPoint(
  geometry: Geometry,
  targetGeometry: Geometry,
): Point | null {
  if (geometry instanceof Point) {
    return geometry;
  } else if (geometry instanceof Polygon) {
    return geometry.centroid;
  } else if (geometry instanceof Polyline) {
    const targetPoint = (targetGeometry as Polygon).centroid;
    const nearestResult = geometryEngine.nearestCoordinate(
      geometry,
      targetPoint,
    );
    return nearestResult.coordinate;
  } else {
    const extent = geometry.extent;
    if (extent) {
      return {
        x: (extent.xmin + extent.xmax) / 2,
        y: (extent.ymin + extent.ymax) / 2,
        spatialReference: geometry.spatialReference,
      } as Point;
    }
  }

  return null;
}

function getVectorDirection(
  fromGeometry: Geometry,
  toGeometry: Geometry,
): string | null {
  const fromPoint = getCenterPoint(fromGeometry, toGeometry);
  const toPoint = getCenterPoint(toGeometry, fromGeometry);
  if (!fromPoint || !toPoint) return null;

  const dx = toPoint.x - fromPoint.x;
  const dy = toPoint.y - fromPoint.y;

  let angleRadians = Math.atan2(dx, dy);
  let angleDegrees = angleRadians * (180 / Math.PI);

  if (angleDegrees < 0) {
    angleDegrees += 360;
  }

  const directions = [
    "north",
    "northeast",
    "east",
    "southeast",
    "south",
    "southwest",
    "west",
    "northwest",
  ];

  const index = Math.round((angleDegrees + 22.5) / 45) % 8;

  return directions[index];
}
